import { JobDocumentTemplates } from "@/components/DocumentTemplateSelect";
// Shared "BILLING" form fragment — used by both the full-page Job Create
// flow and the Job Edit dialog (Jobs.jsx JobForm), so "create" and "edit
// billing" always look and behave identically.
import { useState } from "react";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Receipt, Hammer, Truck, Boxes, Ban } from "lucide-react";

const BILLING_TYPES = [
  { value: "fixed_price", label: "Fixed Price", desc: "One agreed price — invoiced from quote / progress claims" },
  { value: "charge_up", label: "Charge-Up / T&M", desc: "Bill approved labour, plant & materials as they're used" },
  { value: "internal", label: "Internal", desc: "Non-billable — no customer invoicing on this job" },
  { value: "cost_plus", label: "Cost Plus", desc: "Coming later", disabled: true },
];

const FREQUENCIES = [
  { value: "manual", label: "Manual — I'll invoice when ready" },
  { value: "weekly", label: "Weekly" },
  { value: "fortnightly", label: "Fortnightly" },
  { value: "monthly", label: "Monthly" },
  { value: "completion", label: "At job completion" },
];

export function JobBillingSection({ value, onChange, isExistingJob = false, originalBillingType = "fixed_price" }) {
  const [showMigration, setShowMigration] = useState(false);
  const sources = value.charge_up_sources || { labour: true, plant: true, materials: true };

  const setType = (t) => {
    if (isExistingJob && t === "charge_up" && originalBillingType !== "charge_up") {
      setShowMigration(true);
      onChange({ ...value, billing_type: t, charge_up_start_date: value.charge_up_start_date || new Date().toISOString().slice(0, 10) });
    } else {
      onChange({ ...value, billing_type: t });
    }
  };
  const toggleSource = (key) => onChange({ ...value, charge_up_sources: { ...sources, [key]: !sources[key] } });

  return (
    <div data-testid="job-billing-section">
      <JobDocumentTemplates value={value} onChange={onChange} isExistingJob={isExistingJob} />
      <fieldset className="mb-5 space-y-2" data-testid="job-travel-billing">
        <legend className="label-tag mb-2">Travel time billing</legend>
        <p className="text-sm font-medium">{isExistingJob ? "Bill travel time to client" : "Is travel time chargeable to the client?"}</p>
        <div className="flex flex-wrap gap-4">
          {[[true, isExistingJob ? "ON — Chargeable" : "Yes — Bill travel time"], [false, isExistingJob ? "OFF — Not chargeable" : "No — Do not bill travel time"]].map(([choice, label]) => <label key={String(choice)} className="inline-flex min-h-11 items-center gap-2 text-sm cursor-pointer">
            <input type="radio" name="bill_travel_time" checked={value.bill_travel_time === choice} onChange={() => onChange({...value, bill_travel_time: choice})} className="accent-[#FF5F15]" />{label}
          </label>)}
        </div>
        {isExistingJob && value.bill_travel_time == null && <p className="text-xs text-zinc-500">Existing job: current per-timesheet travel billing decisions remain in use until you choose ON or OFF.</p>}
        <p className="text-xs text-zinc-500">{isExistingJob ? "Travel is always retained for timesheets and internal job costing. This controls client billing only." : "Workers can still record travel time. This setting only controls whether travel hours are included in client billing."}</p>
      </fieldset>
      <div className="label-tag mb-2 flex items-center gap-1.5"><Receipt className="w-3 h-3" /> Billing</div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {BILLING_TYPES.map((bt) => (
          <button key={bt.value} type="button" disabled={bt.disabled} onClick={() => setType(bt.value)}
            data-testid={`job-billing-type-${bt.value}`}
            className={`text-left p-2.5 border-2 rounded-md transition-colors ${bt.disabled ? "opacity-40 cursor-not-allowed border-zinc-200 dark:border-white/10" :
              value.billing_type === bt.value ? "border-[#FF5F15] bg-[#FF5F15]/[0.06]" : "border-zinc-200 dark:border-white/10 hover:border-zinc-300"}`}>
            <div className="text-[10px] font-bold uppercase tracking-wider">{bt.label}</div>
            <div className="text-[10px] text-zinc-500 mt-0.5 leading-snug">{bt.desc}</div>
          </button>
        ))}
      </div>

      {value.billing_type === "charge_up" && (
        <div className="mt-3 border-2 border-[#FF5F15]/25 bg-[#FF5F15]/[0.04] rounded-md p-3 space-y-3" data-testid="job-charge-up-settings">
          <div className="text-[10px] font-bold uppercase tracking-widest text-[#FF5F15]">Charge-Up Settings</div>

          <div>
            <div className="text-[9px] font-bold uppercase tracking-widest text-zinc-500 mb-1.5">Billable sources</div>
            <div className="flex flex-wrap gap-3">
              <SourceToggle icon={<Hammer className="w-3 h-3" />} label="Labour / approved timesheets" checked={sources.labour !== false} onChange={() => toggleSource("labour")} testid="job-charge-up-source-labour" />
              <SourceToggle icon={<Truck className="w-3 h-3" />} label="Plant / equipment usage" checked={sources.plant !== false} onChange={() => toggleSource("plant")} testid="job-charge-up-source-plant" />
              <SourceToggle icon={<Boxes className="w-3 h-3" />} label="Materials" checked={sources.materials !== false} onChange={() => toggleSource("materials")} testid="job-charge-up-source-materials" />
              <SourceToggle icon={<Ban className="w-3 h-3" />} label="Subcontractor / other costs" checked={false} onChange={() => {}} disabled testid="job-charge-up-source-other" />
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <div className="text-[9px] font-bold uppercase tracking-widest text-zinc-500 mb-1.5">Billing rates</div>
              <Select value="standard" disabled>
                <SelectTrigger className="h-9 rounded-md text-xs" data-testid="job-charge-up-rate-source"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="standard">Standard Charge-Out Rates</SelectItem></SelectContent>
              </Select>
              <p className="text-[9px] text-zinc-500 mt-1">Team & fleet charge-out rates, with this job's rate as fallback</p>
            </div>
            <div>
              <div className="text-[9px] font-bold uppercase tracking-widest text-zinc-500 mb-1.5">Invoice frequency</div>
              <Select value={value.invoice_frequency || "manual"} onValueChange={(v) => onChange({ ...value, invoice_frequency: v })}>
                <SelectTrigger className="h-9 rounded-md text-xs" data-testid="job-charge-up-invoice-frequency"><SelectValue /></SelectTrigger>
                <SelectContent>{FREQUENCIES.map((f) => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>

          {showMigration && (
            <div className="border-t border-[#FF5F15]/20 pt-3" data-testid="job-charge-up-migration">
              <div className="text-[9px] font-bold uppercase tracking-widest text-zinc-500 mb-1.5">Start tracking Charge-Up from</div>
              <div className="flex items-center gap-2">
                <button type="button" onClick={() => onChange({ ...value, charge_up_start_date: new Date().toISOString().slice(0, 10) })}
                  className={`px-2.5 py-1.5 text-[10px] font-bold uppercase tracking-wider rounded-md border-2 ${value.charge_up_start_date === new Date().toISOString().slice(0, 10) ? "border-[#FF5F15] bg-[#FF5F15]/10" : "border-zinc-200 dark:border-white/10"}`}
                  data-testid="job-charge-up-start-today">Today</button>
                <Input type="date" value={value.charge_up_start_date || ""} onChange={(e) => onChange({ ...value, charge_up_start_date: e.target.value })}
                  className="h-8 rounded-md text-xs w-40" data-testid="job-charge-up-start-date" />
              </div>
              <p className="text-[9px] text-zinc-500 mt-1">Historical timesheets/materials before this date will never count as unbilled.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function SourceToggle({ icon, label, checked, onChange, disabled, testid }) {
  return (
    <label className={`flex items-center gap-1.5 text-xs ${disabled ? "opacity-40" : "cursor-pointer"}`}>
      <Checkbox checked={checked} onCheckedChange={onChange} disabled={disabled} data-testid={testid} />
      {icon} {label}
    </label>
  );
}

export function jobBillingSummary(value) {
  if (value.billing_type !== "charge_up") return null;
  const s = value.charge_up_sources || { labour: true, plant: true, materials: true };
  const bill = [s.labour !== false && "Labour", s.plant !== false && "Plant", s.materials !== false && "Materials"].filter(Boolean);
  const freq = FREQUENCIES.find((f) => f.value === (value.invoice_frequency || "manual"))?.label || "Manual";
  return { bill, freq };
}
